// src/pages/UpdateEmail.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InlineVerificationCode from "../components/VerifyCodePanel";
import Config from "../utils/Config";
import { fireConfetti, displayMessage } from "../utils/functions";
import { AuthContext } from "../context/AuthContext";
import useTitle from "../hooks/useTitle";
import Spinner from "../utils/spinner";
import "./Login.css";
import "./UpdateEmail.css";

const UpdateEmail = () => {
    useTitle("Update Email");

    const { user, login } = useContext(AuthContext);

    const messageRef = useRef(null);
    const emailRef = useRef(null);
    const confirmEmailRef = useRef(null);

    const [formData, setFormData] = useState({ email: "", confirmEmail: "" });
    const [messageClass, setMessageClass] = useState("hidden");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // modal + MFA state (like Account)
    const [modalOpen, setModalOpen] = useState(false);
    const [challengeId, setChallengeId] = useState(null);
    const [codeExpiresAt, setCodeExpiresAt] = useState(null);
    const [pendingMessage, setPendingMessage] = useState("");
    const [pendingStyle, setPendingStyle] = useState("warning");
    const [isLockedOut, setIsLockedOut] = useState(false);

    // confirm dialog (are you sure update?)
    const [confirmOpen, setConfirmOpen] = useState(false);

    // helpers
    const emailMatch =
        formData.email.trim() !== "" &&
        formData.email.trim() === formData.confirmEmail.trim();
    const sameAsCurrent =
        user?.email?.trim().toLowerCase() === formData.email.trim().toLowerCase();

    useEffect(() => {
        emailRef.current?.focus();
    }, []);

    const validate = () => {
        const errs = [];
        if (!formData.email.trim()) errs.push("Email is required.");
        if (!formData.confirmEmail.trim()) errs.push("Confirm email is required.");
        if (!emailMatch) errs.push("Emails do not match.");
        if (sameAsCurrent) errs.push("New email must be different from current email.");
        return errs;
    };

    const onField = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const openConfirm = (e) => {
        e.preventDefault();
        const errs = validate();
        if (errs.length) {
            displayMessage(
                messageRef,
                "❌ " + errs.join("<br/>"),
                "warning",
                setMessageClass
            );
            return;
        }
        setConfirmOpen(true);
    };

    const closeConfirm = () => setConfirmOpen(false);

    const startUpdate = async () => {
        // submit to INIT route -> expects { ok, challengeId, codeExpiresAt }
        setIsSubmitting(true);
        setConfirmOpen(false);
        try {
            const r = await fetch(
                `${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_UPDATE_EMAIL_INIT}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                    body: JSON.stringify({ newEmail: formData.email }),
                }
            );
            const data = await r.json().catch(() => ({}));

            if (!r.ok) {
                if (r.status === 429) {
                    const ra = r.headers.get("Retry-After");
                    displayMessage(
                        messageRef,
                        data?.message || `❌ Too many requests. Try again${ra ? ` in ${ra}s` : ""}.`,
                        "warning",
                        setMessageClass
                    );
                } else {
                    displayMessage(
                        messageRef,
                        data?.message || "❌ Could not start email update.",
                        "warning",
                        setMessageClass
                    );
                }
                return;
            }

            if (!data.challengeId || !data.codeExpiresAt) {
                displayMessage(
                    messageRef,
                    "❌ Malformed response (missing challenge).",
                    "warning",
                    setMessageClass
                );
                return;
            }

            // show modal (same as Account)
            setChallengeId(data.challengeId);
            setCodeExpiresAt(data.codeExpiresAt);
            setPendingMessage("✅ Verification code sent. Please check your new inbox.");
            setPendingStyle("success");
            setModalOpen(true);
        } catch (err) {
            displayMessage(
                messageRef,
                "❌ Server error. Please try again.",
                "warning",
                setMessageClass
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerifySubmit = async (code) => {
        try {
            const r = await fetch(
                `${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_UPDATE_EMAIL_VERIFY}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                    body: JSON.stringify({ challengeId, code }),
                }
            );
            const data = await r.json().catch(() => ({}));

            if (r.status === 429) {
                setIsLockedOut(true);
                displayMessage(
                    messageRef,
                    data?.message || "❌ Too many attempts. Try again later.",
                    "warning",
                    setMessageClass
                );
                return;
            }

            if (!r.ok) {
                displayMessage(
                    messageRef,
                    data?.message || "❌ Invalid or expired code.",
                    "warning",
                    setMessageClass
                );
                return;
            }

            // success: backend should return new auth token and user
            if (data?.token) {
                localStorage.setItem("authToken", data.token);
                login(data.token); // refresh context
            }

            fireConfetti();
            setModalOpen(false);

            setFormData({ email: "", confirmEmail: "" });
            setIsLockedOut(false);
            setChallengeId(null);
            setCodeExpiresAt(null);

            displayMessage(
                messageRef,
                "🎉 Email updated successfully.",
                "success",
                setMessageClass
            );
        } catch (err) {
            displayMessage(
                messageRef,
                "❌ Server error. Try again later.",
                "warning",
                setMessageClass
            );
        }
    };

    const handleVerifyResend = async () => {
        try {
            const r = await fetch(
                `${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_UPDATE_EMAIL_RESEND}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                    body: JSON.stringify({ challengeId }),
                }
            );
            const data = await r.json().catch(() => ({}));

            if (!r.ok) {
                if (r.status === 429) {
                    const ra = r.headers.get("Retry-After");
                    displayMessage(
                        messageRef,
                        data?.message || `❌ Too many requests. Try again${ra ? ` in ${ra}s` : ""}.`,
                        "warning",
                        setMessageClass
                    );
                } else {
                    displayMessage(
                        messageRef,
                        data?.message || "❌ Failed to resend code.",
                        "warning",
                        setMessageClass
                    );
                }
                return;
            }

            if (data?.codeExpiresAt) setCodeExpiresAt(data.codeExpiresAt);

            setPendingMessage("✅ A new verification code was sent.");
            setPendingStyle("success");
            displayMessage(
                messageRef,
                "✅ A new verification code was sent.",
                "success",
                setMessageClass
            );
        } catch (err) {
            displayMessage(
                messageRef,
                "❌ Error contacting server.",
                "warning",
                setMessageClass
            );
        }
    };

    const isSubmitDisabled =
        !formData.email.trim() ||
        !formData.confirmEmail.trim() ||
        !emailMatch ||
        sameAsCurrent;

    return (
        <div className="login-container login-container-dashboard update-email-page">
            <div className="login-box">
                <h2 className="dashboard-title">
                    <span className="titleIcon"><FontAwesomeIcon icon={"envelope"} /></span>
                    &nbsp;&nbsp;Update Email
                </h2>

                <div ref={messageRef} className={messageClass} id="message" />

                <form onSubmit={openConfirm}>
                    <div className="password-container">
                        <input
                            type="email"
                            name="current_email"
                            placeholder="Current Email"
                            autoComplete="current_email"
                            readOnly
                            disabled
                            value={user?.email || ""}
                        />
                    </div>

                    <div className="password-container">
                        <input
                            ref={emailRef}
                            type="email"
                            name="email"
                            placeholder="New Email"
                            autoComplete="email"
                            value={formData.email}
                            onChange={onField}
                        />
                    </div>

                    <div className="password-container">
                        <input
                            ref={confirmEmailRef}
                            type="email"
                            name="confirmEmail"
                            placeholder="Confirm New Email"
                            autoComplete="email"
                            value={formData.confirmEmail}
                            onChange={onField}
                        />
                    </div>

                    {formData.confirmEmail ? (
                        sameAsCurrent ? (
                            <p className="email-match no-match spacer">
                                ❌ New email can’t be the same as your current email
                            </p>
                        ) : (
                            <p className={`email-match ${emailMatch ? "match" : "no-match"}`}>
                                {emailMatch
                                    ? "✅ New & confirm email match"
                                    : "❌ New & confirm email do not match"}
                            </p>
                        )
                    ) : null}

                    <button
                        type="submit"
                        className="login-button update-email-button"
                        disabled={isSubmitDisabled || isSubmitting}
                    >
                        {isSubmitting ? "Working..." : "Update Email"}
                    </button>
                </form>
            </div>

            {/* Confirm modal */}
            {confirmOpen && (
                <div className="modal-overlay">
                    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="update-email-title">
                        <h3 id="update-email-title">Update your Email</h3>
                        <p>Are you sure you want to update it to:</p>
                        <p className="update-email-title">{formData.email.trim()}</p>
                        <br />
                        <div className="modal-buttons">
                            <button type="button" onClick={startUpdate} className="confirm-delete">
                                Yes, Update it
                            </button>
                            <button type="button" onClick={closeConfirm} className="cancel-delete">
                                No, Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* OTP modal (same UX as Account) */}
            {modalOpen && (
                <div className="modal-overlay">
                    <div className="modal modal-account">
                        <InlineVerificationCode
                            onSubmit={handleVerifySubmit}
                            onResend={handleVerifyResend}
                            messageClass={messageClass}
                            messageRef={messageRef}
                            emailInputRef={{ current: null }}
                            codeExpiresAt={codeExpiresAt}
                            setExpiresAt={setCodeExpiresAt}
                            setPendingMessage={setPendingMessage}
                            setMessageClass={setMessageClass}
                            setPendingStyle={setPendingStyle}
                            email={formData.email}
                            isLockedOut={isLockedOut}
                            parent="update_email"
                            mfaAction="update_email"
                            mfaChallengeId={challengeId}
                            pendingMessage={pendingMessage}
                        />

                        <div className="modal-actions">
                            <button
                                type="button"
                                className="cancel-verify-btn"
                                onClick={() => {
                                    setModalOpen(false);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Spinner show={isSubmitting} />
        </div>
    );
};

export default UpdateEmail;